import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import WelcomeModal from '../components/WelcomeModal';
import RegulatoryAlertBanner from '../components/RegulatoryAlertBanner';
import PRICING_CONFIG from '../config/pricing';
import { 
  FiPlus, FiUsers, FiTarget, FiFileText, FiTrendingUp, 
  FiArrowRight, FiZap, FiCheckCircle, FiBarChart2, FiAlertTriangle
} from 'react-icons/fi';

function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const FOUNDER_EMAIL = 'nishantvarma143@gmail.com';
  const isFounder = user?.email === FOUNDER_EMAIL;
  
  const [stats, setStats] = useState({
    clients: 0,
    campaigns: 0,
    content: 0,
    approved: 0,
    usage: { ideas_generated: 0 },
    limits: { ideas_equivalent: PRICING_CONFIG.FREE_POSTS_EQUIVALENT },
    credits: { 
      used: 0, 
      limit: PRICING_CONFIG.FREE_CREDITS, 
      remaining: PRICING_CONFIG.FREE_CREDITS, 
      ideas_equivalent: PRICING_CONFIG.FREE_POSTS_EQUIVALENT 
    },
    tier: 'free',
    free_credits_used: 0
  });
  const [recentClients, setRecentClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    loadDashboard();
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const status = await api.getOnboardingStatus();
      // Show welcome modal for new users who haven't seen it
      if (status.is_new_user && !status.has_seen_welcome) {
        setShowWelcome(true);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  const loadDashboard = async () => {
    try {
      const [clients, contentResponse, usage] = await Promise.all([
        api.listClients(),
        api.listContentIdeas({}, 1, 1),
        api.getUserUsage()
      ]);

      const campaignPromises = clients.map(client => 
        api.listCampaigns(client.id).catch(() => [])
      );
      const campaignResults = await Promise.all(campaignPromises);
      const totalCampaigns = campaignResults.reduce((sum, campaigns) => sum + campaigns.length, 0);

      setStats({
        clients: clients.length,
        campaigns: totalCampaigns,
        content: contentResponse.total || 0,
        approved: 0,
        usage: usage.usage,
        limits: usage.limits,
        credits: usage.credits,
        tier: usage.tier || 'free',
        free_credits_used: usage.free_credits_used || 0
      });
      
      setRecentClients(clients.slice(0, 3));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate usage - using credits system
  const isFreeUser = stats.tier === 'free';
  const creditsUsed = stats.credits.used || 0;
  const creditsLimit = stats.credits.limit || PRICING_CONFIG.FREE_CREDITS;
  const ideasEquivalent = stats.credits.ideas_equivalent || stats.limits.ideas_equivalent || PRICING_CONFIG.FREE_POSTS_EQUIVALENT;
  const usagePercentage = Math.min(100, Math.round((creditsUsed / creditsLimit) * 100));
  const creditsRemaining = stats.credits.remaining || Math.max(0, creditsLimit - creditsUsed);
  
  // Credit warning thresholds
  const isLowCredits = usagePercentage >= 70 && usagePercentage < 90;
  const isCriticalCredits = usagePercentage >= 90;
  const isOutOfCredits = creditsRemaining === 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up">
      {/* Regulatory Alert Banner */}
      <RegulatoryAlertBanner />
      
      {/* Credit Warning Banners */}
      {isOutOfCredits && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center justify-between animate-pulse-subtle">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <FiAlertTriangle className="text-red-500" size={20} />
            </div>
            <div>
              <p className="font-medium text-red-500">You&apos;ve used all your {isFreeUser ? 'free' : 'monthly'} credits!</p>
              <p className="text-muted-foreground text-sm">
                {isFreeUser 
                  ? 'Upgrade to Solo (₹999/mo) for 25 ideas/month with AI learning.' 
                  : 'Upgrade your plan or wait for monthly reset to continue generating.'}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/usage')}
            className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            Upgrade Now <FiArrowRight size={14} />
          </button>
        </div>
      )}
      
      {isCriticalCredits && !isOutOfCredits && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <FiAlertTriangle className="text-amber-500" size={20} />
            </div>
            <div>
              <p className="font-medium text-amber-500">Only {creditsRemaining} credit{creditsRemaining !== 1 ? 's' : ''} remaining!</p>
              <p className="text-muted-foreground text-sm">
                {isFreeUser 
                  ? "You're almost out of free credits. Upgrade to keep creating." 
                  : "Running low on monthly credits. Consider upgrading."}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/usage')}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            See Plans <FiArrowRight size={14} />
          </button>
        </div>
      )}
      
      {isLowCredits && !isCriticalCredits && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <FiZap className="text-blue-500" size={20} />
            </div>
            <div>
              <p className="font-medium text-blue-500">{creditsRemaining} credits remaining this {isFreeUser ? 'trial' : 'month'}</p>
              <p className="text-muted-foreground text-sm">You&apos;ve used {usagePercentage}% of your allowance.</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/usage')}
            className="text-blue-500 text-sm font-medium hover:underline flex items-center gap-1"
          >
            View Usage <FiArrowRight size={14} />
          </button>
        </div>
      )}

      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-muted-foreground mt-1">
            Content strategy that maintains creative intent
          </p>
        </div>
        {/* Only show Add Brand if user has no brands OR is founder */}
        {(stats.clients === 0 || isFounder) && (
          <button
            onClick={() => navigate('/clients')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/20"
            data-testid="add-brand-btn"
          >
            <FiPlus size={18} />
            {stats.clients === 0 ? 'Set Up Your Brand' : 'Add Brand'}
          </button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: FiUsers, label: 'Your Brands', value: stats.clients, color: 'primary' },
          { icon: FiTarget, label: 'Campaigns', value: stats.campaigns, color: 'accent' },
          { icon: FiFileText, label: 'Content Ideas', value: stats.content, color: 'emerald' },
          { icon: FiCheckCircle, label: 'Approval Rate', value: stats.content > 0 ? '—' : '—', color: 'amber' },
        ].map((stat, index) => {
          const colorClasses = {
            primary: 'bg-primary/10 text-primary',
            accent: 'bg-accent/10 text-accent',
            emerald: 'bg-emerald-500/10 text-emerald-500',
            amber: 'bg-amber-500/10 text-amber-500',
          };
          
          return (
            <div 
              key={index} 
              className="group bg-card rounded-xl p-5 border border-border/50 hover:border-primary/30 transition-all hover-lift"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-muted-foreground text-sm mb-1">{stat.label}</p>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-foreground">{stat.value}</p>
                </div>
                <div className={`p-2.5 rounded-lg ${colorClasses[stat.color]}`}>
                  <stat.icon size={20} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* D2C Growth Banner - Show for users with at least 1 brand */}
      {stats.clients >= 1 && ['free', 'solo', 'pro'].includes(user?.subscription_tier?.toLowerCase()) && (
        <div className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/20 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-500/20 rounded-lg">
              <FiZap className="text-violet-400" size={20} />
            </div>
            <div>
              <p className="font-medium text-foreground">Need more credits?</p>
              <p className="text-muted-foreground text-sm">Upgrade your pack for more content generation power.</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/usage')}
            className="px-4 py-2 bg-violet-500 text-white rounded-lg text-sm font-medium hover:bg-violet-600 transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            See Plans <FiArrowRight size={14} />
          </button>
        </div>
      )}

      {/* Usage Progress */}
      <div className="bg-card rounded-xl p-6 border border-border/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 rounded-lg">
              <FiBarChart2 className="text-primary" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{isFreeUser ? 'Free Credits' : 'Monthly Usage'}</h3>
              <p className="text-muted-foreground text-sm">
                {isFreeUser ? 'One-time credits (never expire)' : 'Ideas generated this month'}
              </p>
            </div>
          </div>
          <button 
            onClick={() => navigate('/usage')}
            className="text-primary text-sm font-medium hover:underline flex items-center gap-1"
          >
            View Details <FiArrowRight size={14} />
          </button>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {stats.usage.ideas_generated || 0} of {ideasEquivalent} ideas used
            </span>
            <span className={`font-medium ${usagePercentage >= 80 ? 'text-amber-500' : 'text-foreground'}`}>
              {usagePercentage}%
            </span>
          </div>
          <div className="w-full bg-secondary rounded-full h-2.5">
            <div 
              className={`h-2.5 rounded-full transition-all duration-500 ${
                usagePercentage >= 90 ? 'bg-red-500' : 
                usagePercentage >= 70 ? 'bg-amber-500' : 'bg-primary'
              }`}
              style={{ width: `${usagePercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Clients */}
        <div className="bg-card rounded-xl border border-border/50 overflow-hidden">
          <div className="p-5 border-b border-border/50 flex items-center justify-between">
            <h2 className="font-heading font-semibold text-foreground flex items-center gap-2">
              <FiUsers className="text-primary" size={18} />
              Recent Clients
            </h2>
            <button 
              onClick={() => navigate('/clients')}
              className="text-primary text-sm font-medium hover:underline flex items-center gap-1"
            >
              View All <FiArrowRight size={14} />
            </button>
          </div>
          
          {recentClients.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <FiUsers className="text-muted-foreground" size={24} />
              </div>
              <p className="text-muted-foreground mb-4">No clients yet</p>
              <button
                onClick={() => navigate('/clients')}
                className="text-primary text-sm font-medium hover:underline"
              >
                Add your first client
              </button>
            </div>
          ) : (
            <div className="divide-y divide-border/30">
              {recentClients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => navigate(`/clients/${client.id}`)}
                  className="w-full p-4 flex items-center gap-4 hover:bg-secondary/50 transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-semibold text-sm">
                      {client.name?.charAt(0) || 'C'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{client.name}</p>
                    <p className="text-muted-foreground text-sm truncate">{client.industry || 'No industry set'}</p>
                  </div>
                  <FiArrowRight className="text-muted-foreground" size={16} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick Start Guide */}
        <div className="bg-card rounded-xl border border-border/50 overflow-hidden">
          <div className="p-5 border-b border-border/50">
            <h2 className="font-heading font-semibold text-foreground flex items-center gap-2">
              <FiZap className="text-primary" size={18} />
              Quick Start Guide
            </h2>
          </div>
          
          <div className="p-5 space-y-4">
            {[
              { step: 1, title: 'Create a client profile', desc: 'Add brand context, tone, and constraints', done: stats.clients > 0 },
              { step: 2, title: 'Launch a campaign', desc: 'Set objectives and creative angles', done: stats.campaigns > 0 },
              { step: 3, title: 'Define content focus', desc: 'Visual direction and tone for content', done: false },
              { step: 4, title: 'Generate content', desc: 'AI creates unique ideas with images', done: stats.content > 0 },
            ].map((item) => (
              <div 
                key={item.step}
                className={`flex items-start gap-4 p-3 rounded-lg transition-colors ${
                  item.done ? 'bg-emerald-500/5' : 'hover:bg-secondary/50'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  item.done 
                    ? 'bg-emerald-500/20 text-emerald-500' 
                    : 'bg-secondary text-muted-foreground'
                }`}>
                  {item.done ? <FiCheckCircle size={16} /> : <span className="text-sm font-semibold">{item.step}</span>}
                </div>
                <div>
                  <p className={`font-medium ${item.done ? 'text-emerald-500' : 'text-foreground'}`}>
                    {item.title}
                  </p>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Welcome Modal for New Users */}
      <WelcomeModal 
        isOpen={showWelcome} 
        onClose={() => {
          setShowWelcome(false);
          loadDashboard(); // Refresh dashboard after modal closes
        }}
        userName={user?.name}
      />
    </div>
  );
}

export default Dashboard;
